import { useEffect, useRef } from 'react';
import { useEditorStore } from '@/store/editorStore';

interface UseTouchCanvasOptions {
  svgRef: React.RefObject<SVGSVGElement | null>;
  panX: number;
  panY: number;
  zoom: number;
  setPan: (x: number, y: number) => void;
  onContextMenu: (x: number, y: number, nodeId: string) => void;
  onDoubleTapNode: (nodeId: string) => void;
}

/** Distance between two touch points. */
function touchDistance(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Midpoint between two touch points. */
function touchMidpoint(a: Touch, b: Touch): { x: number; y: number } {
  return {
    x: (a.clientX + b.clientX) / 2,
    y: (a.clientY + b.clientY) / 2,
  };
}

/** Walk up the DOM from an element to find a node with data-node-id. */
function findNodeId(el: Element | null): string | null {
  let current = el;
  while (current) {
    if (current instanceof HTMLElement || current instanceof SVGElement) {
      const nodeId = current.getAttribute('data-node-id');
      if (nodeId) return nodeId;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Custom hook that attaches native touch event listeners to the SVG canvas.
 *
 * Uses native listeners (not React synthetic events) so we can specify
 * `{ passive: false }` and call `e.preventDefault()` to prevent browser
 * scroll/zoom during canvas pan and pinch gestures.
 *
 * Implements:
 * - One-finger pan (drag on background)
 * - Two-finger pinch-to-zoom (zoom toward midpoint)
 * - Long-press on node -> context menu (500ms hold)
 * - Double-tap on node -> edit mode (<300ms between taps)
 */
export function useTouchCanvas(options: UseTouchCanvasOptions): void {
  const {
    svgRef,
    panX,
    panY,
    zoom,
    setPan,
    onContextMenu,
    onDoubleTapNode,
  } = options;

  // Store current values in refs so event handlers always have fresh data
  // without needing to re-attach listeners on every state change.
  const panRef = useRef({ panX, panY, zoom });
  panRef.current = { panX, panY, zoom };

  const setPanRef = useRef(setPan);
  setPanRef.current = setPan;

  const onContextMenuRef = useRef(onContextMenu);
  onContextMenuRef.current = onContextMenu;

  const onDoubleTapNodeRef = useRef(onDoubleTapNode);
  onDoubleTapNodeRef.current = onDoubleTapNode;

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    // -- Panning state --
    let isPanning = false;
    let panStartX = 0;
    let panStartY = 0;
    let panStartPanX = 0;
    let panStartPanY = 0;

    // -- Pinch state --
    let isPinching = false;
    let pinchStartDist = 0;
    let pinchStartZoom = 0;
    let pinchStartPanX = 0;
    let pinchStartPanY = 0;
    let pinchStartMidX = 0;
    let pinchStartMidY = 0;

    // -- Long-press state --
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;
    let longPressNodeId: string | null = null;
    let longPressTouchX = 0;
    let longPressTouchY = 0;
    let longPressFired = false;

    // -- Double-tap state --
    let lastTapTime = 0;
    let lastTapNodeId: string | null = null;

    function clearLongPress() {
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      longPressNodeId = null;
      longPressFired = false;
    }

    function onTouchStart(e: TouchEvent) {
      const touches = e.touches;

      if (touches.length === 2) {
        // -- Start pinch-to-zoom --
        e.preventDefault();
        clearLongPress();
        isPanning = false;
        isPinching = true;

        const t0 = touches[0]!;
        const t1 = touches[1]!;
        pinchStartDist = touchDistance(t0, t1);
        pinchStartZoom = panRef.current.zoom;
        pinchStartPanX = panRef.current.panX;
        pinchStartPanY = panRef.current.panY;
        const mid = touchMidpoint(t0, t1);
        pinchStartMidX = mid.x;
        pinchStartMidY = mid.y;
        return;
      }

      if (touches.length === 1) {
        const touch = touches[0]!;
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const nodeId = target ? findNodeId(target) : null;

        // Check if touch is on the SVG background (not a node)
        const isBackground =
          target === el ||
          (target instanceof SVGElement && target.classList.contains('canvas-bg'));

        if (isBackground) {
          // -- Start panning --
          e.preventDefault();
          isPanning = true;
          panStartX = touch.clientX;
          panStartY = touch.clientY;
          panStartPanX = panRef.current.panX;
          panStartPanY = panRef.current.panY;

          // Clear selection when tapping background (mirrors mouse behavior)
          useEditorStore.getState().clearSelection();
        }

        if (nodeId) {
          // -- Start long-press timer --
          longPressNodeId = nodeId;
          longPressTouchX = touch.clientX;
          longPressTouchY = touch.clientY;
          longPressFired = false;

          longPressTimer = setTimeout(() => {
            if (longPressNodeId) {
              longPressFired = true;
              onContextMenuRef.current(longPressTouchX, longPressTouchY, longPressNodeId);
            }
          }, 500);

          // -- Double-tap detection --
          const now = Date.now();
          if (lastTapNodeId === nodeId && now - lastTapTime < 300) {
            // Double-tap detected
            clearLongPress();
            onDoubleTapNodeRef.current(nodeId);
            lastTapTime = 0;
            lastTapNodeId = null;
          } else {
            lastTapTime = now;
            lastTapNodeId = nodeId;
          }
        } else {
          // Tapped on background, reset double-tap tracking
          lastTapTime = 0;
          lastTapNodeId = null;
        }
      }
    }

    function onTouchMove(e: TouchEvent) {
      const touches = e.touches;

      if (isPinching && touches.length === 2) {
        e.preventDefault();
        clearLongPress();

        const t0 = touches[0]!;
        const t1 = touches[1]!;
        const currentDist = touchDistance(t0, t1);
        const currentMid = touchMidpoint(t0, t1);

        // Calculate new zoom level
        const scale = currentDist / pinchStartDist;
        const newZoom = Math.max(0.1, Math.min(3, pinchStartZoom * scale));

        // Zoom toward the midpoint between fingers.
        // The midpoint in screen space should map to the same world-space
        // point before and after the zoom change.
        //
        // World-space point under midpoint at start:
        //   wx = (midX - panX) / zoom
        // After zoom change, we want the same wx under the current midpoint:
        //   newPanX = currentMidX - wx * newZoom
        const wx = (pinchStartMidX - pinchStartPanX) / pinchStartZoom;
        const wy = (pinchStartMidY - pinchStartPanY) / pinchStartZoom;

        // Also account for finger translation (the midpoint may have moved)
        const newPanX = currentMid.x - wx * newZoom;
        const newPanY = currentMid.y - wy * newZoom;

        useEditorStore.getState().setZoom(newZoom);
        setPanRef.current(newPanX, newPanY);
        return;
      }

      if (isPanning && touches.length === 1) {
        e.preventDefault();
        const touch = touches[0]!;
        const dx = touch.clientX - panStartX;
        const dy = touch.clientY - panStartY;
        setPanRef.current(panStartPanX + dx, panStartPanY + dy);
      }

      // Cancel long-press if finger moved more than 10px
      if (longPressNodeId && touches.length === 1) {
        const touch = touches[0]!;
        const dx = touch.clientX - longPressTouchX;
        const dy = touch.clientY - longPressTouchY;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
          clearLongPress();
        }
      }
    }

    function onTouchEnd(e: TouchEvent) {
      // If long-press context menu was just shown, prevent the default
      // action and any synthesized click event from firing.
      if (longPressFired) {
        e.preventDefault();
        clearLongPress();
      } else {
        clearLongPress();
      }

      // If we were pinching and now have fewer than 2 fingers, stop pinch.
      // If one finger remains, transition into panning from that finger's
      // current position.
      if (isPinching) {
        isPinching = false;
        if (e.touches.length === 1) {
          const touch = e.touches[0]!;
          isPanning = true;
          panStartX = touch.clientX;
          panStartY = touch.clientY;
          panStartPanX = panRef.current.panX;
          panStartPanY = panRef.current.panY;
        } else {
          isPanning = false;
        }
        return;
      }

      if (e.touches.length === 0) {
        isPanning = false;
      }
    }

    function onTouchCancel() {
      isPanning = false;
      isPinching = false;
      clearLongPress();
    }

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    el.addEventListener('touchcancel', onTouchCancel, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
      clearLongPress();
    };
    // Re-attach only when the SVG element reference changes (i.e. on mount).
    // All mutable state is read through refs, so we don't need deps for
    // panX/panY/zoom/callbacks.
  }, [svgRef]);
}
